import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Input, Steps, Typography } from "antd";
import {
  ArrowLeftOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { useDispatch, useSelector } from "react-redux";
import { setAuth, setUser } from "../redux/commonSlice";
import AuthService from "../services/auth.services";
import "./login.css";

const { Title, Text } = Typography;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [forgetMode, setForgetMode] = useState(false);
  const [forgetStep, setForgetStep] = useState(1);
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpPassword, setFpPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpMessage, setFpMessage] = useState("");

  const currentUser = useSelector(state => state.common.user);

  const resetForgotFlow = () => {
    setForgetStep(1);
    setFpOtp("");
    setFpPassword("");
    setFpConfirmPassword("");
    setFpMessage("");
  };

  const openForgotPassword = () => {
    setForgetMode(true);
    resetForgotFlow();
  };

  const backToLoginMode = () => {
    setForgetMode(false);
    resetForgotFlow();
  };

  const onChangeUsername = (e) => {
    const username = e.target.value;
    setUsername(username);
  };

  const onChangePassword = (e) => {
    const password = e.target.value;
    setPassword(password);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setMessage("");
    setLoading(true);
    if (!username.trim() || !password.trim()) {
      setMessage("Username and password are required.");
      setLoading(false);
      return;
    }

    try {
      dispatch(setAuth({ isAuthenticating: true, isAuthenticated: false }));
      const authRes = await AuthService.login(username, password);

      const user = AuthService.getCurrentUser();
      dispatch(setUser(user));
      dispatch(setAuth({ isAuthenticating: false, isAuthenticated: !!user }));

      if (authRes?.accessToken) {
        navigate("/app");
      } else {
        const resMessage =
          (authRes.response && authRes.response.data && authRes.response.data.message) ||
          authRes.message ||
          authRes.toString();
        setMessage(resMessage);
      }
    } catch (error) {
      console.log(error);
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setMessage(resMessage);
      dispatch(setAuth({ isAuthenticating: false, isAuthenticated: false }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      navigate("/app");
    }
  }, [currentUser, navigate]);

  const handleSendOtp = async () => {
    if (!fpEmail.trim()) {
      setFpMessage("Please enter your email.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.forgetPassword(fpEmail.trim());
      setForgetStep(2);
      setFpMessage("OTP sent. Please check your email.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!fpOtp.trim()) {
      setFpMessage("Please enter OTP.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.verifyOTP(fpEmail.trim(), fpOtp.trim());
      setForgetStep(3);
      setFpMessage("OTP verified. You can now set a new password.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!fpPassword || !fpConfirmPassword) {
      setFpMessage("Please enter and confirm your new password.");
      return;
    }

    if (fpPassword !== fpConfirmPassword) {
      setFpMessage("Passwords do not match.");
      return;
    }

    if (fpPassword.length < 6) {
      setFpMessage("Password must be at least 6 characters.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.updatePassword(fpEmail.trim(), fpPassword);
      setForgetMode(false);
      setForgetStep(1);
      setFpOtp("");
      setFpPassword("");
      setFpConfirmPassword("");
      setMessage("Password updated. Please login with your new password.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-bg-orb auth-bg-orb-one" />
      <div className="auth-bg-orb auth-bg-orb-two" />

      <Card className="auth-card" bordered={false}>
        <div className="auth-brand">
          <img src="/branding/onechain-logo.png" alt="OneChain" className="auth-logo" />
          <Title level={2} className="auth-title">OneChain API Platform</Title>
          <Text className="auth-subtitle">Sign in to manage endpoints, API keys, and traffic analytics.</Text>
        </div>

        {!forgetMode ? (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field">
              <label htmlFor="username">Username</label>
              <Input
                id="username"
                size="large"
                prefix={<UserOutlined />}
                placeholder="Enter your username"
                value={username}
                onChange={onChangeUsername}
                autoComplete="username"
              />
            </div>

            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <Input.Password
                id="password"
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                value={password}
                onChange={onChangePassword}
                autoComplete="current-password"
              />
            </div>

            <Button type="primary" htmlType="submit" size="large" loading={loading} className="auth-submit-btn" block>
              Login
            </Button>

            <div className="auth-links">
              <Button type="link" onClick={openForgotPassword} className="auth-link-btn">Forgot password?</Button>
            </div>

            {message && <Alert type="error" message={message} showIcon />}
          </form>
        ) : (
          <div className="auth-forgot-wrap">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              className="auth-back-btn"
              onClick={backToLoginMode}
            >
              Back to login
            </Button>

            <Title level={4} className="auth-forgot-title">Reset Password</Title>
            <Steps
              size="small"
              current={forgetStep - 1}
              className="auth-step"
              items={[
                { title: "Email" },
                { title: "OTP" },
                { title: "New Password" },
              ]}
            />

            <div className="auth-field">
              <label htmlFor="fpEmail">Email</label>
              <Input
                id="fpEmail"
                size="large"
                prefix={<MailOutlined />}
                placeholder="Enter your account email"
                value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)}
                disabled={forgetStep > 1 || fpLoading}
              />
            </div>

            {forgetStep >= 2 && (
              <div className="auth-field">
                <label htmlFor="fpOtp">OTP</label>
                <Input
                  id="fpOtp"
                  size="large"
                  prefix={<KeyOutlined />}
                  placeholder="Enter OTP from email"
                  value={fpOtp}
                  onChange={(e) => setFpOtp(e.target.value)}
                  disabled={forgetStep > 2 || fpLoading}
                />
              </div>
            )}

            {forgetStep >= 3 && (
              <>
                <div className="auth-field">
                  <label htmlFor="fpPassword">New Password</label>
                  <Input.Password
                    id="fpPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="At least 6 characters"
                    value={fpPassword}
                    onChange={(e) => setFpPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="fpConfirmPassword">Confirm New Password</label>
                  <Input.Password
                    id="fpConfirmPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Re-enter new password"
                    value={fpConfirmPassword}
                    onChange={(e) => setFpConfirmPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>
              </>
            )}

            <div className="auth-forgot-actions">
              {forgetStep === 1 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleSendOtp}>
                  Send OTP
                </Button>
              )}

              {forgetStep === 2 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleVerifyOtp}>
                  Verify OTP
                </Button>
              )}

              {forgetStep === 3 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleResetPassword}>
                  Update Password
                </Button>
              )}
            </div>

            {fpMessage && <Alert type="info" message={fpMessage} showIcon />}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Login;
